import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { commissionPlansApi } from '@/api/commissionPlans';
import { mapFieldErrors } from '@/api/fieldErrors';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { ApiErrorState } from '@/components/ApiErrorState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { useActionError, type ActionError } from '@/components/useActionError';
import { useFieldErrors } from '@/components/useFieldErrors';
import { useRemote } from '@/components/useRemote';
import { useToast } from '@/components/useToast';
import { useCanWrite } from '@/auth/authHooks';
import { StatusChip } from '@/components/status/StatusChip';
import { DATE_ONLY, formatInstantOrDash } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { Icon, resolveTokens } from '@/theme';
import { useMenuFocusReturn } from '@/features/members/useMenuFocusReturn';
import { AffiliatePerformancePanel } from './AffiliatePerformancePanel';
import { AffiliateEarningsPanel } from './AffiliateEarningsPanel';
import { CodesPanel } from './CodesPanel';
import { DealEditor } from './DealEditor';
import { ReferralsTable } from './ReferralsTable';
import { SignupsBars } from './SignupsBars';
import { isPlanRequired, planRefOf } from './affiliatePlan';
import type { AffiliateState } from './useAffiliate';

export interface AffiliateDetailProps {
  state: AffiliateState;
  currency: string;
  onClose: () => void;
  onListReload: () => void;
  /** "Set up deal" from the list: the deal editor focuses its plan select once. */
  focusDeal?: boolean;
}

const loadPlans = () => commissionPlansApi.listPlans();

const RENAME_FIELDS = ['displayName'] as const;

export function AffiliateDetail({
  state,
  currency,
  onClose,
  onListReload,
  focusDeal = false,
}: AffiliateDetailProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const showToast = useToast();
  const describe = useActionError();
  const canWrite = useCanWrite();
  const renameRef = useRef<HTMLInputElement>(null);
  const renameButton = useRef<HTMLButtonElement>(null);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState('');
  const [confirm, setConfirm] = useState<'pause' | 'end' | 'decline' | null>(null);
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState<ActionError | null>(null);
  const [renameError, setRenameError] = useState<ActionError | null>(null);
  const renameFields = useFieldErrors(RENAME_FIELDS, { displayName: renameRef }, acting);
  // Leaving the rename (saved or cancelled) removes the field that had focus: hand it to the
  // button that opened it, never <body> (A11Y-010).
  const wasRenaming = useRef(false);
  useEffect(() => {
    if (wasRenaming.current && !renaming) renameButton.current?.focus();
    wasRenaming.current = renaming;
  }, [renaming]);
  // Pause, End and Decline change the status, so their button goes: focus returns to the heading.
  const heading = useRef<HTMLHeadingElement>(null);
  const focusReturn = useMenuFocusReturn(heading);
  // The reseller's plans for the deal's picker, loaded once with the affiliate (TMMRSL-0050).
  const plans = useRemote(loadPlans);
  const approveReasonId = useId();
  const dash = t('affiliates.unavailable');

  if (state.status === 'loading' || state.status === 'idle') {
    return (
      <Box role="status" aria-label={t('common.loading')}>
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="rounded" height={160} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (state.status === 'error') {
    return (
      <ApiErrorState what={t('errors.what.affiliate')} error={state.error} onRetry={state.reload} />
    );
  }

  const detail = state.data?.detail;
  if (!detail) return null;
  const display = detail.displayName;
  // Approving needs a ready agreement that names a plan (TMMAPI-0104). While the deal loads,
  // another affiliate's agreement is not on screen (TMMRSL-0056).
  const agreementReady = state.agreement.status === 'ready';
  const hasPlan = agreementReady && planRefOf(state.data?.agreement?.inForce) !== null;
  const agreementLoading = state.agreement.status === 'loading' && state.data?.agreement == null;
  const referralsLoading = state.referrals.status === 'loading' && state.data?.referrals == null;

  /** A failed affiliate action — a missing plan has its own copy. */
  function actionOf(err: unknown): ActionError {
    const action = describe(err);
    return isPlanRequired(err)
      ? { ...action, message: t('affiliates.error.planRequired') }
      : action;
  }

  /** A quiet refresh after a missing-plan answer (TMMRSL-0053): no skeleton, so the deal editor keeps unsaved edits. */
  function refreshIfPlanRequired(err: unknown) {
    if (isPlanRequired(err)) state.refresh().catch(() => undefined);
  }

  /** The one-click actions (Approve, Resume): the error is the page's alert. */
  async function after(fn: () => Promise<void>, toastKey: string) {
    setActing(true);
    setActionError(null);
    try {
      await fn();
      showToast(t(toastKey, { name: display }));
      onListReload();
    } catch (err) {
      setActionError(actionOf(err));
      refreshIfPlanRequired(err);
    } finally {
      setActing(false);
    }
  }

  const confirmCopy = {
    pause: {
      title: 'affiliates.pauseTitle',
      body: 'affiliates.pauseBody',
      confirm: 'affiliates.pauseConfirm',
      toast: 'affiliates.toast.paused',
    },
    end: {
      title: 'affiliates.endTitle',
      body: 'affiliates.endBody',
      confirm: 'affiliates.endConfirm',
      toast: 'affiliates.toast.ended',
    },
    // Decline is End on an applicant, with the list's copy.
    decline: {
      title: 'affiliates.declineTitle',
      body: 'affiliates.declineBody',
      confirm: 'affiliates.declineConfirm',
      toast: 'affiliates.toast.declined',
    },
  }[confirm ?? 'pause'];

  /** The confirmed actions (Pause, End, Decline): the dialog stays open and shows the error. */
  async function confirmed() {
    const run = confirm === 'pause' ? state.pause : state.end;
    try {
      await run();
    } catch (err) {
      refreshIfPlanRequired(err);
      throw err;
    }
    showToast(t(confirmCopy.toast, { name: display }));
    onListReload();
    focusReturn.rowGone();
  }

  async function handleRename(event: FormEvent) {
    event.preventDefault();
    if (acting) return;
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      renameFields.fail({ displayName: t('validation.name') });
      return;
    }
    setActing(true);
    renameFields.reset();
    setRenameError(null);
    try {
      await state.rename(trimmed);
      showToast(t('affiliates.toast.renamed'));
      setRenaming(false);
      onListReload();
    } catch (err) {
      const action = describe(err);
      const marked = mapFieldErrors(action.fieldKeys, { displayName: 'displayName' });
      // A field error stays on the field; any other failure is an alert above it, with its
      // Reference (TMMRSL-0072).
      if (marked.displayName) renameFields.fail({ displayName: t('affiliates.field.displayName') });
      else setRenameError(action);
    } finally {
      setActing(false);
    }
  }

  return (
    <Box>
      {detail.reviewHold ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>{t('affiliates.reviewHold.title')}</AlertTitle>
          {t('affiliates.reviewHold.body', {
            date: formatInstantOrDash(detail.reviewHold.since, locale, dash, DATE_ONLY),
          })}
          {detail.reviewHold.reason ? (
            <Typography component="blockquote" sx={{ mx: 0, mb: 0 }}>
              {t('affiliates.reviewHold.reason', { reason: detail.reviewHold.reason })}
            </Typography>
          ) : null}
        </Alert>
      ) : null}
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}
      >
        <Box sx={{ minWidth: 0 }}>
          {renaming ? (
            <>
              <ActionErrorAlert error={renameError} sx={{ mb: 1 }} />
              <Stack
                component="form"
                noValidate
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center' }}
                onSubmit={(e) => void handleRename(e)}
                onKeyDown={(e) => {
                  // Escape cancels the edit.
                  if (e.key === 'Escape' && !acting) setRenaming(false);
                }}
              >
                <TextField
                  size="small"
                  required
                  inputRef={renameRef}
                  label={t('affiliates.displayName')}
                  value={name}
                  error={renameFields.errors.displayName !== undefined}
                  helperText={renameFields.errors.displayName}
                  onChange={(e) => {
                    setName(e.target.value);
                    renameFields.clear('displayName');
                  }}
                />
                <SubmitButton type="submit" loading={acting}>
                  {t('affiliates.renameSave')}
                </SubmitButton>
                <Button onClick={() => setRenaming(false)} disabled={acting}>
                  {t('common.cancel')}
                </Button>
              </Stack>
            </>
          ) : (
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <Typography variant="h3" component="h2" tabIndex={-1} ref={heading}>
                {display}
              </Typography>
              {canWrite ? (
                <IconButton
                  ref={renameButton}
                  aria-label={t('affiliates.rename')}
                  onClick={() => {
                    setName(detail.displayName);
                    renameFields.reset();
                    setRenameError(null);
                    setRenaming(true);
                  }}
                >
                  <Icon name="edit" />
                </IconButton>
              ) : null}
            </Stack>
          )}
          {detail.email ? (
            <Typography variant="body2" sx={{ color: tokens.text2 }}>
              {detail.email}
            </Typography>
          ) : null}
          <Box sx={{ mt: 1 }}>
            <StatusChip domain="affiliate" status={detail.status} />
          </Box>
          <Typography variant="body2" sx={{ color: tokens.text2, mt: 1 }}>
            {[
              t('affiliates.appliedAt', { date: formatInstantOrDash(detail.appliedAt, locale, dash, DATE_ONLY) }),
              detail.approvedAt
                ? t('affiliates.approvedAt', { date: formatInstantOrDash(detail.approvedAt, locale, dash, DATE_ONLY) })
                : null,
              detail.endedAt
                ? t('affiliates.endedAt', { date: formatInstantOrDash(detail.endedAt, locale, dash, DATE_ONLY) })
                : null,
            ]
              .filter((part): part is string => part !== null)
              .reduce((a, b) => t('affiliates.dateJoin', { a, b }))}
          </Typography>
        </Box>
        <Button onClick={onClose}>{t('affiliates.closeDetail')}</Button>
      </Stack>
      <ActionErrorAlert error={actionError} sx={{ mt: 2 }} />
      <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
        {canWrite && detail.status === 'APPLIED' ? (
          <>
            {hasPlan ? (
              <Button
                variant="contained"
                onClick={() => void after(state.approve, 'affiliates.toast.approved')}
                disabled={acting}
              >
                {t('affiliates.approve')}
              </Button>
            ) : agreementReady ? (
              <Tooltip title={t('affiliates.approveNeedsPlan')} describeChild>
                <Box component="span" sx={{ display: 'inline-flex' }}>
                  {/* Blocked, not natively disabled: it keeps focus, so the keyboard reaches its
                      tooltip; with no handler, Enter and click do nothing (TMMRSL-0072). */}
                  <Button
                    variant="contained"
                    aria-disabled
                    className="Mui-disabled"
                    aria-describedby={approveReasonId}
                  >
                    {t('affiliates.approve')}
                  </Button>
                </Box>
              </Tooltip>
            ) : (
              <Button variant="contained" disabled>
                {t('affiliates.approve')}
              </Button>
            )}
            <Button
              variant="outlined"
              color="error"
              onClick={(event) => {
                focusReturn.remember(event.currentTarget);
                setConfirm('decline');
              }}
              disabled={acting}
            >
              {t('affiliates.decline')}
            </Button>
          </>
        ) : null}
        {!canWrite ? null : detail.status === 'PAUSED' ? (
          <Button
            onClick={() => void after(state.resume, 'affiliates.toast.resumed')}
            disabled={acting}
          >
            {t('affiliates.resume')}
          </Button>
        ) : detail.status === 'ACTIVE' ? (
          <Button
            onClick={(event) => {
              focusReturn.remember(event.currentTarget);
              setConfirm('pause');
            }}
            disabled={acting}
          >
            {t('affiliates.pause')}
          </Button>
        ) : null}
        {!canWrite || detail.status === 'ENDED' || detail.status === 'APPLIED' ? null : (
          <Button
            color="error"
            onClick={(event) => {
              focusReturn.remember(event.currentTarget);
              setConfirm('end');
            }}
            disabled={acting}
          >
            {t('affiliates.end')}
          </Button>
        )}
      </Stack>
      {canWrite && detail.status === 'APPLIED' && agreementReady && !hasPlan ? (
        // The disabled Approve's reason, visible and its accessible description (TMMRSL-0053).
        <Typography id={approveReasonId} variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          {t('affiliates.approveNeedsPlan')}
        </Typography>
      ) : null}
      <Stack spacing={2} sx={{ mt: 2 }}>
        {state.agreement.status === 'error' ? (
          <ApiErrorState error={state.agreement.error} onRetry={state.agreement.reload} />
        ) : agreementLoading ? (
          <Box role="status" aria-label={t('common.loading')}>
            <Skeleton variant="rounded" height={160} />
          </Box>
        ) : (
          <DealEditor
            history={state.data?.agreement ?? undefined}
            currency={currency}
            plans={plans}
            focusDeal={focusDeal}
            onSave={async (body) => {
              await state.saveDeal(body);
              showToast(t('affiliates.toast.dealSaved'));
              onListReload();
            }}
          />
        )}
        <AffiliatePerformancePanel performance={detail.performance} />
        <AffiliateEarningsPanel key={detail.id} affiliateId={detail.id} />
        <CodesPanel
          codes={detail.codes}
          onCreate={state.createCode}
          onSetStatus={state.setCodeStatus}
          onEditLabel={state.editCodeLabel}
        />
        {state.referrals.status === 'error' ? (
          <ApiErrorState error={state.referrals.error} onRetry={state.referrals.reload} />
        ) : referralsLoading ? (
          <Box role="status" aria-label={t('common.loading')}>
            <Skeleton variant="rounded" height={160} />
          </Box>
        ) : (
          <ReferralsTable
            referrals={state.data?.referrals?.referrals ?? []}
            plans={plans.data}
            onVoid={state.voidReferral}
          />
        )}
        <SignupsBars series={detail.weeklySignups} />
      </Stack>
      <ConfirmDialog
        open={confirm !== null}
        title={t(confirmCopy.title, { name: display })}
        consequence={t(confirmCopy.body)}
        confirmLabel={t(confirmCopy.confirm)}
        destructive={confirm !== 'pause'}
        onConfirm={confirmed}
        onCancel={() => {
          setConfirm(null);
          focusReturn.dialogClosed();
        }}
        errorMessage={(err) => actionOf(err).message}
      />
    </Box>
  );
}
